import { useEffect, useCallback, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Article, Footer, Header, Section, Container, Main } from '@/_containers';
import { CATEGORIES } from '@/_mappers/category-mapper';
import GOOGLE_EVENT_MAPPING from '@/_mappers/google-mappers';
import { ROUTES } from '@/_navigation/routes';
import { useDataContext, useLeadContext, useAjaxHandler } from '@/_provider';
import { TZ } from '@/_static/constants';
import { getDetailedScoresFiltered, getFormData, updateLocalStorage, fixUniqueStages } from '@/_utils';
import { snackbar } from '@/_utils/snackbar-actions';

import {
  CalendlyView,
  Checkout,
  CustomBodyCard,
  FormCard,
  InformativeCard,
  MainCard,
  PageCard,
  ResultsCard,
} from './components';
import { contactSchema } from './components/FormCard/contact-card-schema';

export default function BridgeQualify() {
  const {
    dev_setPortal,
    currentPage,
    currentObj,
    currentQuestions,
    sendGoogleEvent,
    isLoading,
    setIsLoading,
    setEnableButton,
    updateCategory,
    formState: [formData, setFormData],
  } = useDataContext();
  const { navigate, data, noLead } = useLeadContext();
  const { l: leadObj } = data;

  const props = useMemo(() => {
    return {
      navigate,
      currentObj,
      currentPage,
      leadObj,
      currentQuestions,
      sendGoogleEvent,
    };
  }, [currentObj, currentPage, currentQuestions, leadObj, navigate, sendGoogleEvent]);

  const { mutateAsync: mutateLeadQualification, isPending: isPendingLeadQualification } = useAjaxHandler();

  const { mutateAsync: mutateUpdateLead } = useAjaxHandler();

  const form = useForm({
    resolver: zodResolver(contactSchema),
    mode: 'onChange',
    defaultValues: {
      email: leadObj.Email__c ?? '',
      phone: (leadObj.Mobile_Primary__c || '').split('-').join(''),
    },
  });

  const isPending = isPendingLeadQualification;

  const handleGoogleEvent = useCallback(
    (page, isSingle = false) => {
      const ui = localStorage.getItem('ui') ? JSON.parse(localStorage.getItem('ui')) : null;
      const uiArr = ui?.ga_single_events ? ui.ga_single_events : [];
      const googleEvent = GOOGLE_EVENT_MAPPING[page];

      if (isSingle) {
        if (!uiArr.includes(page)) {
          uiArr.push(page);
          updateLocalStorage('ui', { ga_single_events: uiArr });
          sendGoogleEvent(googleEvent);
        }
      } else {
        sendGoogleEvent(googleEvent);
      }
    },
    [sendGoogleEvent]
  );

  const submitLeadQualification = useCallback(
    async (onLoad = false, callback) => {
      if (noLead) {
        return;
      }

      let formData = getFormData();
      const formDataStage = formData?.Stage__c ?? null;
      let stage_score = formDataStage ? `${formDataStage.split(' ').join('_')}_Score__c` : null;

      if (formDataStage) {
        const stageArr = formData.Stages_Completed__c.split(';');
        if (!stageArr.includes(formDataStage)) {
          stageArr.push(formDataStage);
        }
        let Calendly_Appointment_Scheduled__c;
        let Reservation_Confirmed_Score__c;

        updateLocalStorage('formData', {
          Calendly_Appointment_Scheduled__c,
          Reservation_Confirmed_Score__c,
          Stage__c: formDataStage,
          Stages_Completed__c: fixUniqueStages(stageArr),
        });
      }

      formData = getFormData();
      const allScores = getDetailedScoresFiltered(formData);

      if (stage_score) {
        if (formDataStage === CATEGORIES.ENTRANCE_EXAM) {
          stage_score = 'Entrance_Exam_Score__c';
        } else if (formDataStage === CATEGORIES.LICENSURE_EXAM || formDataStage === CATEGORIES.EXIT_EXAM) {
          stage_score = 'Exit_Exam_Score__c';
        } else if (formDataStage === CATEGORIES.CORE_RN_COURSES) {
          stage_score = 'Core_Nursing_Courses_Score__c';
        }

        const score = allScores[formDataStage]?.totalScore;

        updateLocalStorage('formData', { [stage_score]: score });
      }
      if (allScores[CATEGORIES.READINESS]) {
        const score = allScores[CATEGORIES.READINESS]?.totalScore;
        updateLocalStorage('formData', { ['Readiness_Score__c']: score });
      }

      console.log({ stage_score, formDataStage, allScores });

      formData = getFormData();
      const campaign = localStorage.getItem('campaign') ? JSON.parse(localStorage.getItem('campaign')) : null;
      const leadId = formData.Lead__c;

      const leadInfo = {
        firstName: leadObj.First_Name__c,
        lastName: leadObj.Name,
        email: leadObj.Email__c,
        campaign,
        formName: 'Lead Qualification',
        timezone: TZ,
        lq_app: true,
      };

      // remove
      delete formData['BP_Pre-Buy_Res-Meet_Score__c'];
      delete formData['BP_Pre_Buy_Res_Meet_Score__c'];

      const json = {
        action: 'submitLeadQualification',
        leadId,
        userInfo: formData,
        leadInfo,
      };

      mutateLeadQualification(json)
        .then(() => {
          callback?.();
          if (!onLoad && currentObj?.meetAction) {
            return true;
          }
          return null;
        })
        .catch(() => {
          snackbar.error('Something went wrong please try again');
        });
    },
    [currentObj?.meetAction, leadObj.Email__c, leadObj.First_Name__c, leadObj.Name, mutateLeadQualification, noLead]
  );

  const pageCallBack = useCallback(
    (key, value) => {
      if (key && value) {
        setFormData({ ...formData, [key]: value });
      }
    },
    [setFormData, formData]
  );

  const postProcessHandleClick = useCallback(
    (hasCondition, redirectPage, selectedOption) => {
      if (!hasCondition) {
        navigate(currentObj?.nextPage);
      } else {
        updateCategory(currentObj.category);
        handleGoogleEvent(currentPage);
        setIsLoading(true);

        if (selectedOption?.action === 'HAVE_A_MEETING') {
          submitLeadQualification(false, () => {
            setIsLoading(false);
            navigate(`${selectedOption.page}/${leadObj.token__c}`, {
              state: { from: currentPage, dev_setPortal },
            });
          });
        } else if (selectedOption?.action === 'SCHEDULE_MEETING') {
          submitLeadQualification(false, () => {
            setIsLoading(false);
            navigate(selectedOption.page);
          });
        } else {
          submitLeadQualification(false, () => {
            setIsLoading(false);

            if (selectedOption?.action === 'LAUNCH_CHAT') {
              window.open(window.location.origin + ROUTES.CHAT.link, '_self');
            } else if (selectedOption?.action === 'HAVE_A_CALL') {
              window.open(`tel:${(import.meta.env.VITE_PHONE_NUMBER || '').replace(/\D/g, '')}`, '_self');
            } else if (selectedOption?.action === 'LINK') {
              window.open(selectedOption?.externalLink, '_self');
            } else {
              navigate(redirectPage);
            }
          });
        }
      }
    },
    [
      currentObj.category,
      currentObj?.nextPage,
      currentPage,
      dev_setPortal,
      handleGoogleEvent,
      leadObj.token__c,
      navigate,
      setIsLoading,
      submitLeadQualification,
      updateCategory,
    ]
  );

  const handleClick = useCallback(async () => {
    setEnableButton(false);
    // this logic handles conditional page redirects and normal page navigation
    let hasCondition = false;
    let redirectPage = null;
    const uiObj = localStorage.getItem('ui') ? JSON.parse(localStorage.getItem('ui')) : {};
    const currentOptionValue = uiObj?.currentOptionValue ? uiObj.currentOptionValue : null;

    let selectedOption = null;
    if (currentObj?.question) {
      for (const options of currentObj.list) {
        if (options?.conditionals && options?.conditionals.length > 0) {
          for (const option of options.conditionals) {
            const { page, redirect, value } = option;
            if (value === currentOptionValue) {
              selectedOption = option;
              hasCondition = true;
              if (redirect) {
                redirectPage = page;
                break;
              }
            }
          }
        }
      }
    }
    // debugger;
    if (currentObj.allowCategoryAndUpload) {
      // update current stage
      updateCategory(currentObj.category);
      handleGoogleEvent(currentPage);
      setIsLoading(true);
      submitLeadQualification(false, () => {
        setIsLoading(false);
        postProcessHandleClick(hasCondition, redirectPage, selectedOption);
      });
      return;
    }

    postProcessHandleClick(hasCondition, redirectPage, selectedOption);
  }, [
    setEnableButton,
    currentObj?.question,
    currentObj.allowCategoryAndUpload,
    currentObj.list,
    currentObj.category,
    postProcessHandleClick,
    updateCategory,
    handleGoogleEvent,
    currentPage,
    setIsLoading,
    submitLeadQualification,
  ]);

  const processPhoneNumberValidity = useCallback(async () => {
    return true;
  }, []);

  const submitLeadContact = useCallback(async () => {
    setIsLoading(true);
    const formData = getFormData();
    const leadId = formData.Lead__c;

    const userInfo = {
      Id: leadId,
      Email__c: formData.Email__c,
      Mobile_Primary__c: formData.mobile,
    };

    // Validate phone number
    const isPhoneValid = await processPhoneNumberValidity(userInfo.Mobile_Primary__c);
    if (!isPhoneValid) {
      setIsLoading(false);
      snackbar.error('The phone number you entered is invalid. Please try again.');
      return false;
    }

    try {
      await mutateUpdateLead({
        action: 'udpateLead',
        leadId,
        userInfo,
      });
      return true;
    } catch (e) {
      console.log(e);
      snackbar.error('');
      return false;
    }
  }, [mutateUpdateLead, processPhoneNumberValidity, setIsLoading]);

  const handleSubmission = useCallback(
    async (api) => {
      setIsLoading(true);
      switch (api) {
        case 'contact_form':
          {
            const res = await form.trigger(['email', 'phone']);
            if (!res) {
              setIsLoading(false);
              return;
            }

            const isSuccess = await submitLeadContact();
            if (!isSuccess) {
              return;
            }
            updateCategory(currentObj.category);
            submitLeadQualification(false, () => {
              setIsLoading(false);
              navigate(ROUTES.BRIDGE_RESULTS.link);
            });
          }
          break;

        case 'ATP_13__c':
          submitLeadQualification(false);
          break;
        default:
          break;
      }
      return null;
    },
    [currentObj.category, form, navigate, setIsLoading, submitLeadContact, submitLeadQualification, updateCategory]
  );

  const renderCard = useCallback(() => {
    let comp = null;
    if (currentObj?.pageCard) {
      comp = (
        <PageCard
          {...props}
          isPending={isPending}
          submitLeadQualification={submitLeadQualification}
          handleGoogleEvent={handleGoogleEvent}
          leadObj={leadObj}
          currentPage={currentPage}
        />
      );
    } else if (currentObj?.calendlyComponent) {
      comp = (
        <CalendlyView
          {...props}
          submitLeadQualification={submitLeadQualification}
          handleGoogleEvent={handleGoogleEvent}
          updateCategory={updateCategory}
        />
      );
    } else if (currentObj?.checkout) {
      comp = (
        <Checkout
          {...props}
          buttonIsLoading={isLoading}
          submitLeadQualification={submitLeadQualification}
          handleGoogleEvent={handleGoogleEvent}
        />
      );
    } else if (currentObj?.main) {
      console.log('<--- main card --->');
      comp = <MainCard {...props} />;
    } else if (currentObj?.customBody) {
      console.log('<--- customBody card --->');
      comp = <CustomBodyCard {...props} />;
    } else if (!currentObj?.main && currentObj?.informative && !currentObj?.results && !currentObj?.videoComponent) {
      console.log('<--- InformativeCard card --->');
      comp = <InformativeCard {...props} />;
    } else if (currentObj?.informative && currentObj?.results) {
      console.log('<--- ResultsCard card --->');
      comp = <ResultsCard {...props} />;
    } else {
      console.log('<--- else card --->');
      comp = <FormCard {...props} form={form} pageCallBack={pageCallBack} setEnableButton={setEnableButton} />;
    }
    return comp;
  }, [
    currentObj?.calendlyComponent,
    currentObj?.checkout,
    currentObj?.customBody,
    currentObj?.informative,
    currentObj?.main,
    currentObj?.pageCard,
    currentObj?.results,
    currentObj?.videoComponent,
    currentPage,
    form,
    handleGoogleEvent,
    isLoading,
    isPending,
    leadObj,
    pageCallBack,
    props,
    setEnableButton,
    submitLeadQualification,
    updateCategory,
  ]);

  useEffect(() => {
    if (currentPage === ROUTES.QUALIFY.link) {
      handleGoogleEvent('start_qualify');
      submitLeadQualification(true);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Article>
      <Container className={`${currentObj?.results ? 'results' : ''} ${currentObj.backgroundColor}`}>
        <Section className={currentObj.calendlyComponent ? 'calendly' : ''}>
          <Header />
          <Main className={currentObj.calendlyComponent ? 'calendly' : ''}>{renderCard(currentPage, currentObj)}</Main>
        </Section>
      </Container>
      <Footer handleClick={handleClick} handleSubmission={handleSubmission} />
    </Article>
  );
}
